import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { ProjectImageUploader } from '@/components/ProjectImageUploader';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Calendar, X } from 'lucide-react';
import { format } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { CornerLabelField } from '@/components/ui/corner-label-field';
import { IconButtonCancel, IconButtonSave } from '@/components/ui/icon-buttons';
import { useDateFormat } from '@/utils/date-formatter';
import { projectDateToLocalDate } from '@/utils/project-dates';
import { getSortedCountries, getCountryName } from '@/constants/countries';
import { useProjectStatuses, useProjectStatusDisplay } from '@/hooks/use-project-statuses';
import { useClientTypes, useClientTypeDisplay } from '@/hooks/use-client-types';
import {
  projectFormFieldStyle,
  projectFormInputClassName,
  projectFormSelectTriggerClassName,
} from '@/components/project/ProjectFormCardLayout';

interface ProjectDetailsTabProps {
  project: any;
  tickets: any[];
  documents: any[];
  teamMembers: any[];
  ticketStatusCounts: {
    postponed: number;
    open: number;
    inProgress: number;
    completed: number;
  };
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
  isFormDirty?: boolean;
  onImageUpdate?: (imageUrl: string | null) => void;
  isContractor?: boolean;
  onLogoUpload?: (file: File) => Promise<void>;
  onLogoRemove?: () => Promise<void>;
  
  // Form fields
  projektNummer: string;
  setProjektNummer: (value: string) => void;
  projektName: string;
  setProjektName: (value: string) => void;
  street: string;
  setStreet: (value: string) => void;
  houseNo: string;
  setHouseNo: (value: string) => void;
  addressSupplement: string;
  setAddressSupplement: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  postalCode: string;
  setPostalCode: (value: string) => void;
  country: string;
  setCountry: (value: string) => void;
  latitude: number | null;
  setLatitude: (value: number | null) => void;
  longitude: number | null;
  setLongitude: (value: number | null) => void;
  coordinateWarning?: string;
  weatherValidationError?: string | null;
  isValidatingWeather?: boolean;
  auftraggeber: string;
  setAuftraggeber: (value: string) => void;
  clientTypeId: string | null;
  setClientTypeId: (value: string | null) => void;
  currency: string;
  setCurrency: (value: string) => void;
  projektleiter: string;
  setProjektleiter: (value: string) => void;
  projektleiterId: string | null;
  setProjektleiterId: (value: string | null) => void;
  managerTeamEntryId: string | null;
  setManagerTeamEntryId: (value: string | null) => void;
  
  // Status and dates
  projectStatusId: string | null | undefined;
  onStatusChange: (value: string) => void;
  startDatum: string;
  isEditingStartDate: boolean;
  setIsEditingStartDate: (value: boolean) => void;
  onStartDateChange: (value: string) => void;
  endDatum: string;
  isEditingEndDate: boolean;
  setIsEditingEndDate: (value: boolean) => void;
  onEndDateChange: (value: string) => void;
  
  // Progress
  fortschritt: number;
  setFortschritt: (value: number) => void;
  onProgressChange: (value: number) => void;
}

export const ProjectDetailsTab: React.FC<ProjectDetailsTabProps> = ({
  project,
  tickets,
  documents,
  teamMembers,
  ticketStatusCounts,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  isSaving,
  isFormDirty = false,
  onImageUpdate,
  isContractor = false,
  onLogoUpload,
  onLogoRemove,
  projektNummer,
  setProjektNummer,
  projektName,
  setProjektName,
  street,
  setStreet,
  houseNo,
  setHouseNo,
  addressSupplement,
  setAddressSupplement,
  city,
  setCity,
  postalCode,
  setPostalCode,
  country,
  setCountry,
  latitude,
  setLatitude,
  longitude,
  setLongitude,
  coordinateWarning,
  weatherValidationError,
  isValidatingWeather = false,
  auftraggeber,
  setAuftraggeber,
  clientTypeId,
  setClientTypeId,
  currency,
  setCurrency,
  projektleiter,
  setProjektleiter,
  projektleiterId,
  setProjektleiterId,
  managerTeamEntryId,
  setManagerTeamEntryId,
  projectStatusId,
  onStatusChange,
  startDatum,
  isEditingStartDate,
  setIsEditingStartDate,
  onStartDateChange,
  endDatum,
  isEditingEndDate,
  setIsEditingEndDate,
  onEndDateChange,
  fortschritt,
  setFortschritt,
  onProgressChange
}) => {
  const { data: clientTypes = [] } = useClientTypes();
  const { displayName: clientTypeDisplayName } = useClientTypeDisplay(clientTypeId);
  const { t, i18n } = useTranslation(['pages.projectDetails', 'pages.projects']);
  const { formatDate } = useDateFormat();

  // Helper function to get translated client type name
  const getClientTypeDisplayName = (clientTypeName: string): string => {
    const clientTypeMap: Record<string, string> = {
      'Bay': t('pages.projects:clientType.bay'),
      'Bayan': t('pages.projects:clientType.bayan'),
      'Firma': t('pages.projects:clientType.firma'),
    };
    return clientTypeMap[clientTypeName] || clientTypeName;
  };

  // Load project statuses from database
  const { data: projectStatuses = [] } = useProjectStatuses();
  const { displayName: statusDisplayName, color: statusColor } = useProjectStatusDisplay(projectStatusId);

  // Load project members for manager select
  const projectId = project?.id || null;
  const { data: membersResponse } = useQuery({
    queryKey: ["/api/projects", projectId, "members"],
    queryFn: async () => {
      const res = await apiRequest(`/api/projects/${projectId}/members`);
      return await res.json();
    },
    enabled: !!projectId,
    staleTime: 60_000,
    retry: false,
  });
  const memberOptions: { id: string; name: string; linkedUserId: string | null }[] = (((membersResponse as any)?.data || []) as any[])
    .map((m: any) => {
      const id = m.teamEntryId || m.team_entry_id || m.id || null;
      const linkedUserId = m.linkedUserId || m.userId || m.user_id || null;
      const name = m.displayName || m.display_name || `${m.firstName || m.first_name || ''} ${m.lastName || m.last_name || ''}`.trim() || m.email || '';
      return id && name ? { id, name, linkedUserId } : null;
    })
    .filter(Boolean)
    .sort((a: { id: string; name: string; linkedUserId: string | null }, b: { id: string; name: string; linkedUserId: string | null }) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    ) as { id: string; name: string; linkedUserId: string | null }[];
  React.useEffect(() => {
    if (!projectId || !membersResponse) return;
    const hasValidManager = !!managerTeamEntryId && memberOptions.some((member) => member.id === managerTeamEntryId);
    if (hasValidManager) return;
    if (!managerTeamEntryId && !projektleiter && !projektleiterId) return;

    setManagerTeamEntryId(null);
    setProjektleiterId(null);
    setProjektleiter('');
  }, [
    managerTeamEntryId,
    memberOptions,
    membersResponse,
    projectId,
    projektleiter,
    projektleiterId,
    setManagerTeamEntryId,
    setProjektleiter,
    setProjektleiterId,
  ]);

  return (
    <div className="p-4 pt-2 -mt-[8px]">
      {/* Main Content - Two Column Layout */}
      <div className="max-w-[1112px]">
        <div className="card-container card-container-muted card-container-no-shadow p-[32px]">
          {!isContractor && (
            <div className="flex items-center justify-end gap-2 mb-4">
              <IconButtonCancel
                onClick={onCancel}
                disabled={!isFormDirty}
                size={30}
                title={t('form.cancel')}
              />
              <IconButtonSave
                onClick={onSave}
                disabled={isSaving || !isFormDirty}
                size={30}
                title={isSaving ? t('form.saving') : t('form.save')}
              />
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column - Form Fields */}
        <div className="space-y-4">
          <CornerLabelField label={t('form.projectNumber')}>
            {isEditing ? (
              <input
                type="text"
                value={projektNummer}
                onChange={(e) => setProjektNummer(e.target.value)}
                className={projectFormInputClassName}
                style={projectFormFieldStyle}
                placeholder={t('form.enterProjectNumber')}
              />
            ) : (
              <p className="text-xl bg-slate-50 p-3 rounded-lg">
                {projektNummer ? (
                  <span className="text-slate-900">{projektNummer}</span>
                ) : (
                  <span className="text-slate-400">{t('form.notSpecified')}</span>
                )}
              </p>
            )}
          </CornerLabelField>
          <CornerLabelField label={t('form.projectName')}>
            {isEditing ? (
              <input
                type="text"
                value={projektName}
                onChange={(e) => setProjektName(e.target.value)}
                className={projectFormInputClassName}
                style={projectFormFieldStyle}
                placeholder={t('form.enterProjectName')}
              />
            ) : (
              <p className="text-xl text-slate-900 bg-slate-50 p-3 rounded-lg">{projektName}</p>
            )}
          </CornerLabelField>
          
          {/* Location Fields */}
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <CornerLabelField label={t('form.street')}>
                {isEditing ? (
                  <input
                    type="text"
                    value={street}
                    onChange={(e) => setStreet(e.target.value)}
                    className="w-full px-3 py-2 text-xl border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-[#818181]"
                    style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '20px' }}
                    placeholder={t('form.streetPlaceholder')}
                  />
                ) : (
                  <p className="text-xl bg-slate-50 p-3 rounded-lg">
                    {street ? (
                      <span className="text-slate-900">{street}</span>
                    ) : (
                      <span className="text-slate-400">{t('form.notSpecified')}</span>
                    )}
                  </p>
                )}
              </CornerLabelField>
              <CornerLabelField label={t('form.houseNo')}>
                {isEditing ? (
                  <input
                    type="text"
                    value={houseNo}
                    onChange={(e) => setHouseNo(e.target.value)}
                    className="w-full px-3 py-2 text-xl border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-[#818181]"
                    style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '20px' }}
                    placeholder={t('form.enterHouseNo')}
                  />
                ) : (
                  <p className="text-xl bg-slate-50 p-3 rounded-lg">
                    {houseNo ? (
                      <span className="text-slate-900">{houseNo}</span>
                    ) : (
                      <span className="text-slate-400">{t('form.notSpecified')}</span>
                    )}
                  </p>
                )}
              </CornerLabelField>
            </div>
            
            <CornerLabelField label={t('form.addressSupplement')}>
              {isEditing ? (
                <input
                  type="text"
                  value={addressSupplement}
                  onChange={(e) => setAddressSupplement(e.target.value)}
                  className="w-full px-3 py-2 text-xl border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-[#818181]"
                  style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '20px' }}
                  placeholder={t('form.enterAddressSupplement')}
                />
              ) : (
                <p className="text-xl bg-slate-50 p-3 rounded-lg">
                  {addressSupplement ? (
                    <span className="text-slate-900">{addressSupplement}</span>
                  ) : (
                    <span className="text-slate-400">{t('form.notSpecified')}</span>
                  )}
                </p>
              )}
            </CornerLabelField>
            
            <div className="grid grid-cols-2 gap-4">
              <CornerLabelField label={t('form.postalCode')}>
                {isEditing ? (
                  <input
                    type="text"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    className="w-full px-3 py-2 text-xl border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-[#818181]"
                    style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '20px' }}
                    placeholder={t('form.enterPostalCode')}
                  />
                ) : (
                  <p className="text-xl bg-slate-50 p-3 rounded-lg">
                    {postalCode ? (
                      <span className="text-slate-900">{postalCode}</span>
                    ) : (
                      <span className="text-slate-400">{t('form.notSpecified')}</span>
                    )}
                  </p>
                )}
              </CornerLabelField>
              <CornerLabelField label={`${t('form.city')} *`}>
                {isEditing ? (
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full px-3 py-2 text-xl border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 placeholder:text-[#818181]"
                    style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '20px' }}
                    placeholder={t('form.enterCity')}
                    required
                  />
                ) : (
                  <p className="text-xl bg-slate-50 p-3 rounded-lg">
                    {city ? (
                      <span className="text-slate-900">{city}</span>
                    ) : (
                      <span className="text-slate-400">{t('form.notSpecified')}</span>
                    )}
                  </p>
                )}
              </CornerLabelField>
            </div>
            
            <CornerLabelField label={`${t('form.country')} *`}>
              {isEditing ? (
                <Select
                  value={country || ''}
                  onValueChange={(value) => setCountry(value)}
                  required
                >
                  <SelectTrigger className="w-full" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '20px' }}>
                    <SelectValue placeholder={t('form.enterCountry')}>
                      {country ? getCountryName(country, i18n.language as 'en' | 'de' | 'tr') : ''}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {getSortedCountries(i18n.language as 'en' | 'de' | 'tr').map((countryItem) => (
                      <SelectItem key={countryItem.code} value={countryItem.code}>
                        {countryItem[i18n.language as 'en' | 'de' | 'tr']}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xl bg-slate-50 p-3 rounded-lg">
                  {country ? (
                    <span className="text-slate-900">{getCountryName(country, i18n.language as 'en' | 'de' | 'tr') || country}</span>
                  ) : (
                    <span className="text-slate-400">{t('form.notSpecified')}</span>
                  )}
                </p>
              )}
            </CornerLabelField>

            {/* Optional Manual Coordinates - Hidden for now, may be needed in future */}
            <details className="mt-2 hidden">
              <summary className="text-xs text-gray-600 cursor-pointer">{t('form.coordinatesOptional')}</summary>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <CornerLabelField label={t('form.latitude')}>
                  {isEditing ? (
                    <input
                      type="number"
                      step="any"
                      value={latitude ?? ''}
                      onChange={(e) => setLatitude(e.target.value ? parseFloat(e.target.value) : null)}
                      className="w-full px-3 py-2 text-xl border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="52.5200"
                    />
                  ) : (
                    <p className="text-xl bg-slate-50 p-3 rounded-lg">
                      {latitude !== null ? (
                        <span className="text-slate-900">{latitude}</span>
                      ) : (
                        <span className="text-slate-400">{t('form.notSpecified')}</span>
                      )}
                    </p>
                  )}
                </CornerLabelField>
                <CornerLabelField label={t('form.longitude')}>
                  {isEditing ? (
                    <input
                      type="number"
                      step="any"
                      value={longitude ?? ''}
                      onChange={(e) => setLongitude(e.target.value ? parseFloat(e.target.value) : null)}
                      className="w-full px-3 py-2 text-xl border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="13.4050"
                    />
                  ) : (
                    <p className="text-xl bg-slate-50 p-3 rounded-lg">
                      {longitude !== null ? (
                        <span className="text-slate-900">{longitude}</span>
                      ) : (
                        <span className="text-slate-400">{t('form.notSpecified')}</span>
                      )}
                    </p>
                  )}
                </CornerLabelField>
              </div>
            </details>

            {coordinateWarning && (
              <div className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                {coordinateWarning}
              </div>
            )}

            {weatherValidationError && (
              <div className="text-xs text-red-800 bg-red-50 p-2 rounded border border-red-200">
                {weatherValidationError}
                {isValidatingWeather && (
                  <p className="text-xs text-red-600 mt-1">{t('form.validating')}</p>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <CornerLabelField label={t('form.clientType')}>
              {isEditing ? (
                <Select 
                  value={clientTypeId || ''} 
                  onValueChange={(value) => setClientTypeId(value || null)}
                >
                  <SelectTrigger className={projectFormSelectTriggerClassName} style={projectFormFieldStyle}>
                    <SelectValue placeholder={t('form.clientTypePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {clientTypes.map((clientType) => (
                      <SelectItem key={clientType.id} value={clientType.id}>
                        {getClientTypeDisplayName(clientType.name)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xl bg-slate-50 p-3 rounded-lg">
                  {clientTypeDisplayName && clientTypeDisplayName !== '-' ? (
                    <span className="text-slate-900">
                      {clientTypeDisplayName}
                    </span>
                  ) : (
                    <span className="text-slate-400">{t('form.notSpecified')}</span>
                  )}
                </p>
              )}
            </CornerLabelField>

            <CornerLabelField label={t('form.client')}>
              {isEditing ? (
                <input
                  type="text"
                  value={auftraggeber}
                  onChange={(e) => setAuftraggeber(e.target.value)}
                  className={projectFormInputClassName}
                  style={projectFormFieldStyle}
                  placeholder={t('form.clientPlaceholder')}
                />
              ) : (
                <p className="text-xl bg-slate-50 p-3 rounded-lg">
                  {auftraggeber ? (
                    <span className="text-slate-900">{auftraggeber}</span>
                  ) : (
                    <span className="text-slate-400">{t('form.notSpecified')}</span>
                  )}
                </p>
              )}
            </CornerLabelField>
          </div>
          <CornerLabelField label={t('form.currency')}>
            {isEditing ? (
              <Select value={currency || "EUR"} onValueChange={setCurrency}>
                <SelectTrigger className={projectFormSelectTriggerClassName} style={projectFormFieldStyle}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="CHF">CHF (CHF)</SelectItem>
                  <SelectItem value="TRY">TRY (₺)</SelectItem>
                  <SelectItem value="PLN">PLN (zł)</SelectItem>
                  <SelectItem value="DKK">DKK (kr)</SelectItem>
                  <SelectItem value="SEK">SEK (kr)</SelectItem>
                  <SelectItem value="NOK">NOK (kr)</SelectItem>
                  <SelectItem value="CZK">CZK (Kč)</SelectItem>
                  <SelectItem value="HUF">HUF (Ft)</SelectItem>
                  <SelectItem value="RON">RON (lei)</SelectItem>
                  <SelectItem value="BGN">BGN (лв)</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xl bg-slate-50 p-3 rounded-lg">
                {currency ? (
                  <span className="text-slate-900">{currency}</span>
                ) : (
                  <span className="text-slate-400">{t('form.notSpecified')}</span>
                )}
              </p>
            )}
          </CornerLabelField>
          <CornerLabelField label={t('form.projectManager')}>
            {isEditing ? (
              <Select
                value={managerTeamEntryId || '__none__'}
                onValueChange={(v) => {
                  if (v === '__none__') {
                    setManagerTeamEntryId(null);
                    setProjektleiterId(null);
                    setProjektleiter('');
                    return;
                  }
                  const selectedMember = memberOptions.find((member) => member.id === v);
                  setManagerTeamEntryId(v);
                  setProjektleiterId(selectedMember?.linkedUserId || null);
                  setProjektleiter(selectedMember?.name || '');
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('form.selectManager')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('form.selectManager')}</SelectItem>
                  {memberOptions.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xl bg-slate-50 p-3 rounded-lg">
                {projektleiter ? (
                  <span className="text-slate-900">{projektleiter}</span>
                ) : (
                  <span className="text-slate-400">{t('form.notAssigned')}</span>
                )}
              </p>
            )}
          </CornerLabelField>
          
          {/* Status field */}
          <CornerLabelField label={t('form.status')}>
            <Select
              value={projectStatusId || ''}
              onValueChange={(value) => onStatusChange(value)}
              disabled={isContractor || !isEditing}
            >
              <SelectTrigger className="w-full" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", fontSize: '20px' }}>
                <SelectValue>
                  {statusDisplayName || '-'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {projectStatuses.map((status) => {
                  // Use semanticType for labels (language-agnostic)
                  const semantic = (status.semanticType || '').toLowerCase();
                  let displayName = status.displayName || status.name;
                  if (semantic === 'active') displayName = t('pages.projects:status.active');
                  else if (semantic === 'paused') displayName = t('pages.projects:status.paused');
                  else if (semantic === 'completed') displayName = t('pages.projects:status.completed');
                  return (
                    <SelectItem key={status.id} value={status.id}>
                      {displayName}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </CornerLabelField>
          
          {/* Start Date and End Date side by side */}
          <div className="grid grid-cols-2 gap-4">
            <CornerLabelField label={t('form.startDate')}>
              {!isContractor ? (
                <Popover>
                  <div className="textbox-base flex h-9 min-h-9 items-center gap-1">
                    <PopoverTrigger asChild>
                      <button
                        className="flex h-9 min-h-9 w-full flex-1 items-center justify-end text-right"
                        type="button"
                      >
                        {startDatum ? (
                          <span className="text-slate-900">{formatDate(startDatum)}</span>
                        ) : (
                          <span className="text-slate-400">{t('form.notSpecified')}</span>
                        )}
                        <Calendar className="ml-2 h-3 w-3 text-slate-500" />
                      </button>
                    </PopoverTrigger>
                    {startDatum ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onStartDateChange('');
                        }}
                        className="inline-flex h-4 w-4 shrink-0 items-center justify-center p-0 leading-none text-slate-500 hover:text-slate-700"
                        aria-label={t('form.clearSelection')}
                        title={t('form.clearSelection')}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    ) : null}
                  </div>
                  <PopoverContent className="w-auto p-4" align="start" side="bottom" sideOffset={8} collisionPadding={16}>
                    <CalendarComponent
                      mode="single"
                      selected={projectDateToLocalDate(startDatum)}
                      onSelect={(date) => {
                        if (date) {
                          onStartDateChange(format(date, "yyyy-MM-dd"));
                        }
                      }}
                      initialFocus
                      className="p-4"
                      classNames={{
                        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                        month: "space-y-4",
                        table: "w-full border-collapse space-y-2",
                        head_row: "flex",
                        head_cell: "text-muted-foreground rounded-md w-12 font-normal !text-base",
                        row: "flex w-full mt-2",
                        cell: "h-12 w-12 text-center text-sm p-0 relative",
                        day: "h-12 w-12 p-0 font-normal aria-selected:opacity-100 !text-lg",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              ) : (
                <p className="text-xl bg-slate-50 p-3 rounded-lg cursor-default">
                  {startDatum ? (
                    <span className="text-slate-900">{formatDate(startDatum)}</span>
                  ) : (
                    <span className="text-slate-400">{t('form.notSpecified')}</span>
                  )}
                </p>
              )}
            </CornerLabelField>
            <CornerLabelField label={t('form.endDate')}>
              {!isContractor ? (
                <Popover>
                  <div className="textbox-base flex h-9 min-h-9 items-center gap-1">
                    <PopoverTrigger asChild>
                      <button
                        className="flex h-9 min-h-9 w-full flex-1 items-center justify-end text-right"
                        type="button"
                      >
                        {endDatum ? (
                          <span className="text-slate-900">{formatDate(endDatum)}</span>
                        ) : (
                          <span className="text-slate-400">{t('form.notSpecified')}</span>
                        )}
                        <Calendar className="ml-2 h-3 w-3 text-slate-500" />
                      </button>
                    </PopoverTrigger>
                    {endDatum ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onEndDateChange('');
                        }}
                        className="inline-flex h-4 w-4 shrink-0 items-center justify-center p-0 leading-none text-slate-500 hover:text-slate-700"
                        aria-label={t('form.clearSelection')}
                        title={t('form.clearSelection')}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    ) : null}
                  </div>
                  <PopoverContent className="w-auto p-4" align="start" side="bottom" sideOffset={8} collisionPadding={16}>
                    <CalendarComponent
                      mode="single"
                      selected={projectDateToLocalDate(endDatum)}
                      onSelect={(date) => {
                        if (date) {
                          onEndDateChange(format(date, "yyyy-MM-dd"));
                        }
                      }}
                      initialFocus
                      className="p-4"
                      classNames={{
                        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                        month: "space-y-4",
                        table: "w-full border-collapse space-y-2",
                        head_row: "flex",
                        head_cell: "text-muted-foreground rounded-md w-12 font-normal !text-base",
                        row: "flex w-full mt-2",
                        cell: "h-12 w-12 text-center text-sm p-0 relative",
                        day: "h-12 w-12 p-0 font-normal aria-selected:opacity-100 !text-lg",
                      }}
                    />
                  </PopoverContent>
                </Popover>
              ) : (
                <p className="text-xl bg-slate-50 p-3 rounded-lg cursor-default">
                  {endDatum ? (
                    <span className="text-slate-900">{formatDate(endDatum)}</span>
                  ) : (
                    <span className="text-slate-400">{t('form.notSpecified')}</span>
                  )}
                </p>
              )}
            </CornerLabelField>
          </div>

          {/* Progress Section */}
          <div>
            <label className="block text-sm font-semibold text-[#505050] mb-2" style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>{t('form.progress')}</label>
            <div className="space-y-2">
              <div className="relative">
                {/* Custom Progress Slider */}
                <div className="relative w-full h-2 bg-white rounded-lg overflow-hidden border border-slate-200">
                  {/* Progress Fill */}
                  <div 
                    className="absolute top-0 left-0 h-full rounded-lg transition-all duration-300 ease-in-out"
                    style={{ width: `${fortschritt}%`, backgroundColor: '#4A90E2' }}
                  />
                  
                  {/* Custom Thumb */}
                  <div 
                    className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow-lg transition-all duration-300 ease-in-out hover:scale-110 hover:shadow-xl"
                    style={{ left: `calc(${fortschritt}% - 8px)`, backgroundColor: '#4A90E2' }}
                  />
                  
                  {/* Slider Track */}
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={fortschritt}
                    onChange={(e) => {
                      const newProgress = parseInt(e.target.value);
                      setFortschritt(newProgress);
                      onProgressChange(newProgress);
                    }}
                    disabled={isContractor}
                    className={`absolute top-0 left-0 w-full h-full opacity-0 z-10 focus:opacity-20 focus:bg-blue-200 focus:rounded-lg ${
                      isContractor ? 'cursor-not-allowed' : 'cursor-pointer'
                    }`}
                    style={{
                      background: 'transparent',
                      WebkitAppearance: 'none',
                      appearance: 'none',
                      outline: 'none',
                      border: 'none'
                    }}
                  />
                </div>
                
                {/* Progress Labels */}
                <div className="flex justify-between text-sm text-slate-600 mt-2">
                  <span>0%</span>
                  <span className="font-medium text-blue-600">
                    {fortschritt}%
                  </span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Project Image and Logo */}
        <div className="space-y-6">
          {/* Project Image */}
          <div>
            {isEditing && onImageUpdate ? (
              <ProjectImageUploader
                projectId={project?.id}
                imageUrl={project?.bildUrl}
                onImageUpdate={onImageUpdate}
                hideLabel={true}
                imageHeight="h-[250px]"
              />
            ) : project?.bildUrl ? (
              <div className="relative">
                <img 
                  src={project.bildUrl} 
                  alt={project.name}
                  className="w-full h-[350px] object-cover rounded-2xl border border-slate-200"
                />
              </div>
            ) : (
              <div className="w-full h-[350px] border-2 border-dashed border-slate-300 rounded-2xl flex items-center justify-center text-slate-500">
                {t('components.projectImageUploader:noImage')}
              </div>
            )}
          </div>

          {/* Project Logo */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">PROJEKT LOGO</label>
            {project?.projektLogo ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-24 h-24 flex items-center justify-center relative">
                  <img
                    src="/images/icons/dashboard_round_border.svg"
                    alt=""
                    className="absolute inset-0 w-full h-full"
                    aria-hidden="true"
                  />
                  <div className="w-20 h-20 rounded-full flex items-center justify-center bg-white overflow-hidden relative z-10">
                    <img
                      src={project.projektLogo}
                      alt="Project logo"
                      className="w-full h-full object-contain p-1"
                    />
                  </div>
                </div>
                {!isContractor && onLogoUpload && onLogoRemove && (
                  <div className="flex gap-2">
                    <Button
                      variant="pillOutline"
                      size="sm"
                      className="text-xs bg-white hover:bg-white"
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = async (e) => {
                          const file = (e.target as HTMLInputElement).files?.[0];
                          if (file && onLogoUpload) {
                            await onLogoUpload(file);
                          }
                        };
                        input.click();
                      }}
                    >
                      {t('settings.logos.replace')}
                    </Button>
                    <Button
                      variant="pillOutline"
                      size="sm"
                      className="text-xs bg-white hover:bg-white"
                      onClick={async () => {
                        if (onLogoRemove) {
                          await onLogoRemove();
                        }
                      }}
                    >
                      {t('settings.logos.remove')}
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="w-24 h-24 flex items-center justify-center relative">
                  <img
                    src="/images/icons/dashboard_round_border.svg"
                    alt=""
                    className="absolute inset-0 w-full h-full"
                    aria-hidden="true"
                  />
                  <div className="w-20 h-20 rounded-full flex items-center justify-center bg-white relative z-10">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#4A90E2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 17L12 22L22 17" stroke="#4A90E2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 12L12 17L22 12" stroke="#4A90E2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
                {!isContractor && onLogoUpload && (
                  <Button
                    variant="pillOutline"
                    size="sm"
                    className="text-xs bg-white hover:bg-white"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = async (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file && onLogoUpload) {
                          await onLogoUpload(file);
                        }
                      };
                      input.click();
                    }}
                  >
                    {t('settings.logos.upload')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
      </div>
    </div>
  );
};
